/**
 * buildQueueJoinSection — builds the `QueueJoinPanel` node for the single
 * currently-open cell of the shared availability-cell popover (CU-86cb7ewja).
 *
 * Shared by the offer-draft availability tab, the inventory catalog, and the
 * media-unit detail calendar — all three render the SAME `InspectPopover`
 * seam, so identity resolution (own-reservation / offer / standalone) stays
 * in ONE place instead of being re-implemented per route.
 */
import type { ReactNode } from 'react';
import {
  QueueJoinPanel,
  type QueueJoinClientOption,
  type QueueJoinOfferOption,
  type QueueJoinIdentity,
  type QueueJoinPanelLabels,
  type QueueJoinPayload,
} from './queue-join-panel';
import type { QueueAction } from '../components/reservation-queue-list';
import type { ActiveCellQueueState } from './use-cell-queue-join';

export interface QueueJoinSectionOfferIdentity {
  offerId: number;
  /** Visible, editable offer/client/brand summary — see `QueueJoinIdentity`'s offer variant. */
  label: string;
}

export interface BuildQueueJoinSectionOptions {
  activeQueueState: ActiveCellQueueState | undefined;
  currentUserId: number;
  /** Namespaces the rendered `ReservationQueueList`'s own test ids — no other semantic meaning. */
  queueListId: number;
  clientOptions: QueueJoinClientOption[];
  offerOptions?: QueueJoinOfferOption[];
  labels: QueueJoinPanelLabels;
  onJoin: (payload: QueueJoinPayload) => void;
  onQueueAction: (action: QueueAction, entryId: number) => void;
  isJoinPending: boolean;
  /** Present ONLY for an offer-scoped caller (the offer-draft availability tab). Omit for the
   * catalog and media-unit detail calendar, which have no offer in scope. */
  offerIdentity?: QueueJoinSectionOfferIdentity;
}

/** Resolves which identity mode `QueueJoinPanel` should use for the open cell's ownership. */
export function resolveQueueJoinIdentity(
  ownership: 'other' | 'mine',
  offerIdentity: QueueJoinSectionOfferIdentity | undefined,
): QueueJoinIdentity {
  if (ownership === 'mine') return { mode: 'own-reservation' };
  if (offerIdentity)
    return { mode: 'offer', offerId: offerIdentity.offerId, label: offerIdentity.label };
  return { mode: 'standalone' };
}

export function buildQueueJoinSection(
  isOpen: boolean,
  testId: string,
  options: BuildQueueJoinSectionOptions,
): ReactNode {
  if (!isOpen || !options.activeQueueState) return undefined;
  const identity = resolveQueueJoinIdentity(
    options.activeQueueState.ownership,
    options.offerIdentity,
  );
  return (
    <QueueJoinPanel
      testId={`${testId}-queue`}
      queueListId={options.queueListId}
      ownEntries={options.activeQueueState.ownEntries}
      currentUserId={options.currentUserId}
      permissions={{ canManageQueue: false, canDisplacePromote: false }}
      identity={identity}
      clientOptions={options.clientOptions}
      offerOptions={options.offerOptions}
      onJoin={options.onJoin}
      onQueueAction={options.onQueueAction}
      isJoinPending={options.isJoinPending}
      labels={options.labels}
    />
  );
}
